import { buildUserAgent, extractErrorMessage, fetchWithTimeout, throwForHttpStatus } from '@wport/core';
import { CliError, ExitCode } from './errors';
import { loadPersonalCredentials, savePersonalCredentials, type PersonalCredentials } from './credentials-store';
import { CLI_SOURCE } from './global-opts';
import { refreshAccessToken } from './oauth';
import { printWarn } from './output';

export interface PersonalRequestOptions {
	baseUrl: string; // resolveContext 產出，已去尾斜線
	locale: string;
	timeoutMs: number;
}

export interface PersonalResult {
	body: unknown;
	headers: Headers;
}

/** 寫入請求可選 headers。resumes 各寫入端點皆帶 Idempotency-Key（create/update 編排每節各自獨立一把）。 */
export interface PersonalWriteExtra {
	idempotencyKey?: string;
}

type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

/** access_token 距 expires_at 不到這個緩衝就視為「即將過期」，提前 refresh 換新，避免發出注定 401 的請求。 */
const EXPIRY_SKEW_MS = 30_000;

const NOT_LOGGED_IN_MESSAGE = 'Not logged in. Run `wport login`.';

/**
 * 確保回傳的 personal credentials 尚未過期：距 expires_at 不到 30 秒（含無法解析成合法時間戳，
 * `Date.parse` 回 NaN 時比較恆為 false）就視為需要 refresh，一律走 refreshAndSave 換新再回傳。
 * personal 區缺席（未登入）→ CliError exit 3，訊息導向 `wport login`。
 */
async function ensureFreshCredentials(opts: PersonalRequestOptions): Promise<PersonalCredentials> {
	const creds = loadPersonalCredentials();
	if (!creds) throw new CliError(NOT_LOGGED_IN_MESSAGE, ExitCode.ServerClientError);
	const msUntilExpiry = Date.parse(creds.expires_at) - Date.now();
	if (msUntilExpiry >= EXPIRY_SKEW_MS) return creds;
	return refreshAndSave(opts, creds);
}

/**
 * 呼叫 refresh grant 換新 token pair 並整份寫回 credentials store；display_name/email/
 * session_created_at 沿舊值（AS 的 TokenResponse 不含身分資訊）。`refreshAccessToken` 對
 * `invalid_grant` 已丟出 CliError exit 3（訊息含 `wport login`），這裡不攔截，讓呼叫端
 * 統一透過同一條錯誤路徑上拋；寫回失敗（磁碟錯誤等）也照常丟錯，不吞。
 */
async function refreshAndSave(opts: PersonalRequestOptions, creds: PersonalCredentials): Promise<PersonalCredentials> {
	const tokens = await refreshAccessToken(opts, creds.refresh_token);
	const updated: PersonalCredentials = {
		...creds,
		access_token: tokens.access_token,
		refresh_token: tokens.refresh_token,
		expires_at: new Date(Date.now() + tokens.expires_in * 1000).toISOString(),
	};
	savePersonalCredentials(updated);
	return updated;
}

interface RawAttemptResult {
	status: number;
	body: unknown;
	headers: Headers;
}

/**
 * 送出單次請求（不含 refresh/retry 邏輯）。headers 形狀同 enterprise-client：UA / Accept-Language /
 * Accept 恆帶；有 body 才帶 Content-Type；`extra.idempotencyKey` 給了才帶 `Idempotency-Key`。
 */
async function attemptRequest(
	opts: PersonalRequestOptions,
	method: HttpMethod,
	path: string,
	accessToken: string,
	body: Record<string, unknown> | undefined,
	query: Record<string, string | number | undefined> | undefined,
	extra: PersonalWriteExtra | undefined
): Promise<RawAttemptResult> {
	const url = new URL(`${opts.baseUrl}${path}`);
	for (const [k, v] of Object.entries(query ?? {})) {
		if (v !== undefined) url.searchParams.set(k, String(v));
	}
	const headers: Record<string, string> = {
		Authorization: `Bearer ${accessToken}`,
		'Accept-Language': opts.locale,
		'User-Agent': buildUserAgent('wport-cli', __CLI_VERSION__),
		'X-Source': CLI_SOURCE,
		Accept: 'application/json',
	};
	if (body !== undefined) headers['Content-Type'] = 'application/json';
	if (extra?.idempotencyKey) headers['Idempotency-Key'] = extra.idempotencyKey;
	const request = new Request(url, {
		method,
		headers,
		body: body !== undefined ? JSON.stringify(body) : undefined,
	});
	const res = await fetchWithTimeout(request, opts.timeoutMs);
	const respBody: unknown = await res.json().catch(() => null);
	return { status: res.status, body: respBody, headers: res.headers };
}

/**
 * personal API 請求共用核心：expires_at 預判 refresh（ensureFreshCredentials）→ 發請求 → 401 →
 * refresh 一次並重試一次（防迴圈：重試後仍非 2xx 就直接丟出，不再迴圈）。refresh 併發單飛不需要
 * 處理——CLI 為單命令生命週期，同一進程內不會有並行請求互搶 refresh。
 */
async function personalRequest(
	opts: PersonalRequestOptions,
	method: HttpMethod,
	path: string,
	body: Record<string, unknown> | undefined,
	query: Record<string, string | number | undefined> | undefined,
	extra: PersonalWriteExtra | undefined
): Promise<PersonalResult> {
	const creds = await ensureFreshCredentials(opts);
	let result = await attemptRequest(opts, method, path, creds.access_token, body, query, extra);
	if (result.status === 401) {
		const refreshed = await refreshAndSave(opts, creds);
		result = await attemptRequest(opts, method, path, refreshed.access_token, body, query, extra);
	}
	if (result.status < 200 || result.status >= 300) throwPersonalHttpError(result.status, result.body);
	warnIfRateLimitLow(result.headers);
	return { body: result.body, headers: result.headers };
}

/** personal API 唯讀 GET。path 為完整路徑（呼叫端自帶 `/api/v1/personal/...` 或 `/oauth/sessions`）。 */
export function personalGet(
	opts: PersonalRequestOptions,
	path: string,
	query?: Record<string, string | number | undefined>
): Promise<PersonalResult> {
	return personalRequest(opts, 'GET', path, undefined, query, undefined);
}

/** personal API 寫入 POST。 */
export function personalPost(
	opts: PersonalRequestOptions,
	path: string,
	body: Record<string, unknown>,
	extra?: PersonalWriteExtra
): Promise<PersonalResult> {
	return personalRequest(opts, 'POST', path, body, undefined, extra);
}

/** personal API 寫入 PATCH（如 published-status toggle）。 */
export function personalPatch(
	opts: PersonalRequestOptions,
	path: string,
	body: Record<string, unknown>,
	extra?: PersonalWriteExtra
): Promise<PersonalResult> {
	return personalRequest(opts, 'PATCH', path, body, undefined, extra);
}

/** personal API 寫入 PUT（如改名、portfolio_links bulk 覆寫）。 */
export function personalPut(
	opts: PersonalRequestOptions,
	path: string,
	body: Record<string, unknown>,
	extra?: PersonalWriteExtra
): Promise<PersonalResult> {
	return personalRequest(opts, 'PUT', path, body, undefined, extra);
}

/** personal API 寫入 DELETE。無 body。 */
export function personalDelete(opts: PersonalRequestOptions, path: string, extra?: PersonalWriteExtra): Promise<PersonalResult> {
	return personalRequest(opts, 'DELETE', path, undefined, undefined, extra);
}

/**
 * 401 訊息改為個人線提示（re-run `wport login`）；其餘狀態碼沿用 `throwForHttpStatus` 通用路徑。
 * 走到這裡代表 refresh + 重試一次後仍失敗（或本來就是非 401 的 4xx/5xx）——不再嘗試第二次 refresh。
 */
function throwPersonalHttpError(status: number, body: unknown): never {
	if (status === 401) {
		const base = extractErrorMessage(body) ?? `HTTP ${status}`;
		throw new CliError(
			`${base} — Your session may have expired or the request was rejected. Run \`wport login\` to sign in again.`,
			ExitCode.ServerClientError
		);
	}
	throwForHttpStatus(status, body);
}

/** 剩餘配額 <10% 時 stderr 提醒（不阻斷）。邏輯複製自 enterprise-client.warnIfRateLimitLow（同款 rate limit headers）。 */
function warnIfRateLimitLow(headers: Headers): void {
	const remaining = Number(headers.get('x-ratelimit-remaining'));
	const limit = Number(headers.get('x-ratelimit-limit'));
	if (Number.isFinite(remaining) && Number.isFinite(limit) && limit > 0 && remaining / limit < 0.1) {
		printWarn(`Rate limit nearly exhausted: ${remaining}/${limit} requests remaining this window.`, false);
	}
}
